// eslint-disable-next-line @typescript-eslint/no-var-requires
const plugin = require('tailwindcss/plugin');

const rem = (value) => value / 16 + 'rem';
const em = (value) => value / 16 + 'em';

/** @type {import('tailwindcss').Config} */
module.exports = {
    content: ['./components/**/*.tsx', './pages/**/*.tsx'],
    theme: {
        colors: {
            primary: '#222',
            white: '#fff',
            blue: '#2d66f9',
            blueLight: '#3a8ff3',
            blueLighter: '#c3e6fa',
            blueVeryLight: '#f7f9fc',
            black: '#000',
            green: '#008060',
            greenLight: '#82be59',
            greenVeryLight: '#cce5df',
            greenDark: '#176652',
            greenInfo: '#cce5df',
            greenContrast: '#4f7d31',
            red: '#bd0d2e',
            redLight: '#f93339',
            redDark: '#892a41',
            redIntense: '#bd0d2e',
            orange: '#d8560d',
            orangeLight: '#ffb053',
            orangeLighter: '#ffebd4',
            orangeInfo: '#f8e4ce',
            yellow: '#fffa00',
            gray: '#ececec',
            grayVeryLight: '#fafbfb',
            grayLight: '#f7f7f7',
            grayLighter: '#f1f2f3',
            grayMidDarker: '#e5e5e5',
            grayDarker: '#8c9196',
            grayDark: '#6d7175',
            grayVeryDark: '#31363a',
            border: '#a6acb6',
            borderLight: '#e2e2e2',
            borderLighter: '#f6f6f7',
            outlineFocus: '#bbe7fc',
            borderFocus: '#2e8efa',
            addToCartLight: '#008060',
            addToCartDark: '#176652',
            springLight: '#5ec224',
            springDark: '#4d9f1d',
            tescomaLight: '#ce152c',
            tescomaDark: '#a71122',
            freeShippingLight: '#224ce2',
            freeShippingDark: '#1738b3',
            tipsOfTheWeekLight: '#fd9b3c',
            tipsOfTheWeekDark: '#f27a02',
            blackFridayDark: '#000000',
            blackFridayLight: '#404040',
            glamourLight: '#dd035c',
            glamourDark: '#b5034a',
            christmasBrownLight: '#806d38',
            christmasBrownDark: '#67562d',
            christmasBeigeLight: '#bea36a',
            christmasBeigeDark: '#a38648',
            christmasGreenLight: '#195240',
            christmasGreenDark: '#12372b',
            inherit: 'inherit',
            transparent: 'transparent',
            currentColor: 'currentColor',
            tableBackground: '#ececec',
            tableBackgroundContrast: '#f7f7f7',
            tableBackgroundHeader: '#f1f2f3',
            tableText: '#222',
            tableTextHeader: '#6d7175',
        },
        fontFamily: {
            base: ['var(--font-roboto)'],
        },
        borderRadius: {
            none: 0,
            full: '100%',
            round: '50%',
            '2xl': '20px',
            '3xl': '24px',
            xl: '13px',
            lg: '11px',
            md: '7px',
            sm: '2px',
        },
        zIndex: {
            hidden: -1000,
            above: 1,
            two: 2,
            productSlider: 480,
            stickyBar: 490,
            belowOverlay: 498,
            overlay: 500,
            menu: 501,
            aboveMenu: 750,
            consentPopup: 800,
            cart: 6000,
            aboveOverlay: 10001,
            maximum: 10100,
            auto: 'auto',
        },
        fontSize: {
            h1: [rem(36)],
            h2: [rem(28)],
            h3: [rem(26)],
            h4: [rem(24)],
            h5: [rem(22)],
            h6: [rem(18)],
            description: [rem(18)],
            body: [rem(16)],
            small: [rem(14)],
            tiny: '0.82rem',
            footer: [rem(11)],
        },
        screens: {
            xs: em(320),
            sm: em(480),
            md: em(600),
            lg: em(769),
            vl: em(1024),
            xl: em(1240),
            xxl: em(1560),
            notSm: { max: em(479) },
            notMd: { max: em(599) },
            notLg: { max: em(768) },
            notVl: { max: em(1023) },
            notXl: { max: em(1239) },
            notXxl: { max: em(1559) },
        },
        extend: {
            lineHeight: {
                12: '3rem',
                2: '0.5rem'
            },
            spacing: {
                layout: '1240px',
                desktop: '720px',
            },
            transitionProperty: {
                height: 'height',
                width: 'width',
            },
            transitionTimingFunction: {
                defaultTransition: 'cubic-bezier(.8, .20, .48, 1.0)',
            },
            backgroundPosition: {
                'center-top': 'center top',
            },
            boxShadow: {
                popup: '0 30px 50px rgba(0, 0, 0, 0.2)',
                select: '0 0 0 3px #bbe7fc',
                input: '0 0 0 1000px #fff inset',
                grayShadow: '0 0 30px rgba(0, 0, 15, 0.1)',
                grayDown: '0 7px 10px rgba(0, 0, 15, 0.1)',
                green: '0 0 0 4px rgba(0, 236, 177, 0.12)',
                redShadow: '0 0 0 3px #fddce2',
                subtle: '0 1px 1px rgba(0, 0, 0, 0.05)',
                menu: '0px 1px 10px 0px #00000059',
                mobileMenu: '0 5px 10px 0 rgba(164, 167, 193, 0.34)',
                mobileSubMenu: '0 20px 20px 0 rgba(0, 0, 0, 0.15)',
                none: '0 0 #0000',
            },
            backgroundSize: {
                6: '1.5rem',
            },
            borderWidth: {
                5: '5px'
            },
            order: {
                0: '0'
            },
            scrollMargin: {
                '16': '4rem',
                '20': '5rem'
            }
        },
    },

    plugins: [
        plugin(({ addVariant, addUtilities, matchUtilities, theme }) => {
            addUtilities({
                '.w-appearance-none': {
                    '-webkit-appearance': 'none;',
                },
            });
            addUtilities({
                '.flex-center': {
                    'justify-content': 'center;',
                    'align-items': 'center;',
                },
            });
            matchUtilities(
                {
                    'bg-pos-x': (value) => ({
                        'background-position-x': value,
                    }),
                },
                { values: theme('backgroundPosition') },
            );
            matchUtilities(
                {
                    'bg-pos-y': (value) => ({
                        'background-position-y': value,
                    }),
                },
                { values: theme('backgroundPosition') },
            );
            addVariant('webkit-scrollbar', '&::-webkit-scrollbar');
            addVariant('webkit-scrollbar-track', '&::-webkit-scrollbar-track');
            addVariant('webkit-scrollbar-thumb', '&::-webkit-scrollbar-thumb');
            addVariant('webkit-autofill', '&:-webkit-autofill');
            addVariant('internal-autofill-selected', '&:-internal-autofill-selected');
            addVariant('webkit-outer-spin-button', '&::-webkit-outer-spin-button');
            addVariant('webkit-inner-spin-button', '&::-webkit-inner-spin-button');
            addVariant('not-last', '&:not(:last-child)');
            addVariant('not-first', '&:not(:first-child)');
        }),
    ],
};
